import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { t } from 'ttag'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { Redirect, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import isFunction from 'lodash/isFunction'

import { Globals } from '@h2/globals'
import { api, errors as apiErrors } from '@h2/api'
import { features } from '@h2/components/media'
import {
  NAVIANCEBLUE,
  PB40T,
  PB90T,
  SDB,
  SDB20S,
  SDB60S,
  SLR10S,
  SLR10T,
  SLR20S,
  SY
} from '@h2/components/colors'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { Button } from '@h2/components/buttons/button'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { Alert } from '@h2/components/display/alert'
import signInSvg from '@h2/assets/svg/illustrations/signIn.svg'
import signInRegisteredSvg from '@h2/assets/svg/illustrations/signInRegistered.svg'
import navianceLinkedAccountSvg from '@h2/assets/svg/illustrations/navianceLinkedAccount.svg'
import { closeDialog, openSigninDialog } from '@main/actions/signinOrRegisterDialog'
import { sessionStarted, startSession, verifyMembership } from '@h2/features/auth'
import { setUser } from '@h2/common/actions/setUser'

import { Card } from './card'
import { Link as LinkComponent } from './link'
import { Form as ResetPasswordForm } from './resetPassword/form'
import { Form as LookupUsernameForm } from './lookupUsername/form'
import { Form as MfaChallengeForm } from './mfaChallenge/form'
import { Wrap } from './registration/highlights/components'
import { useForm } from './hooks/useForm'
import { usePageTitle } from './hooks/usePageTitle'
import { useNavigator } from './hooks/useNavigator'

import { userForgotCredential } from '../actions/userForgotCredential'
import { toggleEmailConfirmationDialog } from '../actions/toggleEmailConfirmationDialog'
import { forgotCredential } from '../selectors/forgotCredential'

const Form = styled.form`
  > div:nth-child(n + 2) {
    margin: 15px 0 0 0;
  }

  > div:last-child {
    margin: 30px 0 0 0;
  }

  @media (${features.minWidth.laptop}) {
    > div:last-child {
      width: 250px;
      margin: 30px auto 0 auto;
    }
  }
`
const Link = styled(LinkComponent)`
  white-space: nowrap;
`
const FlexForgot = styled(Flex)`
  > a:first-child {
    margin-right: 20px;
  }
`
const HighlightWrap = styled(Wrap)`
  ${({ isNavianceUser }) =>
    isNavianceUser &&
    css`
      & h2,
      & p {
        text-align: center;
      }
    `}
`
const FlexWrap = styled(Flex)`
  ${({ isNavianceUser }) =>
    isNavianceUser &&
    css`
      flex-direction: column;
      align-items: center;
    `}
`
const Submit = styled(Button)`
  font-size: 20px;
`
const SocialLink = styled.a`
  color: white;
  background-color: ${({ provider }) => (provider === 'clever' ? SDB : SLR10T)};
  border: 1px solid ${({ provider }) => (provider === 'clever' ? SDB60S : SLR20S)};
  border-radius: 4px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  text-decoration: none;
  font-size: 14px;
  font-weight: bold;
  min-width: 225px;
  flex-basis: max-content;

  &:hover,
  &:focus {
    color: white;
    text-decoration: none;
    background-color: ${({ provider }) => (provider === 'clever' ? SDB20S : SLR10S)};
  }

  > span:first-child {
    font-size: 20px;
    padding: 0 10px 0 0;
  }

  > span:last-child {
    padding: 6px 0 6px 10px;
    border-left: 1px solid ${({ provider }) => (provider === 'clever' ? SDB60S : SLR20S)};
  }
`
const OrBar = styled.p`
  text-align: center;
  margin: 40px 0 0 0;

  &::before {
    content: '';
    display: block;
    height: 1px;
    background: ${PB90T};
    position: relative;
    top: 11px;
  }

  span {
    color: ${PB40T};
    font-size: 14px;
    display: inline-block;
    text-transform: uppercase;
    padding: 0 11px;
    background: white;
    position: relative;
    z-index: 1;
  }
`
const Image = styled.img`
  width: 115px;
  height: auto;

  ${({ isNavianceUser }) =>
    isNavianceUser &&
    css`
      width: 215px;
    `}

  @media (${features.minWidth.laptop}) {
    width: 100%;
  }
`
const SignInForm = ({ properties, addMessage, inDialog, customDetourHandler }) => {
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigator()
  const forgot = useSelector(forgotCredential)
  const [redirect, setRedirect] = useState(null)
  const [signInError, setSignInError] = useState('')
  const [isLinked, setIsLinked] = useState(false)
  const [mfaPending, setMfaPending] = useState(null)
  const {
    userProfile,
    allowInternalSignins,
    allowInternalRegistrations,
    socialLogins,
    systemName,
    isNavianceUser,
    isNavianceElementary,
    environment,
    setViewMode,
    postSigninURI
  } = useContext(Globals)
  const isUserInSessionOnMount = useRef(Boolean(userProfile))
  const provider = 'naviance'
  const { refetch } = useQuery(
    ['islinkedAccount', provider],
    async () => {
      const isLinkedResponse = await api.fetch('/external/authorizations', {
        method: 'GET'
      })

      return isLinkedResponse.items.some(item => item.provider === provider)
    },
    {
      enabled: false,
      onSuccess: async isLinkedQuery => {
        if (isLinkedQuery) {
          await api.fetch('/auth/endsession', {
            method: 'POST'
          })
          dispatch(setUser(null))
        }
      }
    }
  )
  const { register, error, handleOnSubmit, isInvalid, setValue, setValidation } = useForm(
    {
      name(form) {
        if (!form.name?.trim()) {
          return t`Username is required`
        }
      },
      password(form) {
        if (!form.password?.trim()) {
          return t`Password is required`
        }
      }
    }
  )
  const [lastUsername, setLastUsername] = useState('')
  const { mutate: signIn, isLoading } = useMutation(
    form => {
      setLastUsername(form.name)

      return api.fetch('/auth/login', { method: 'POST', body: JSON.stringify(form) })
    },
    {
      onSuccess: async ({ user, membership, mfaPending: mfaResponse }) => {
        if (mfaResponse) {
          setMfaPending(mfaResponse)

          return
        }

        if (user) {
          const showConfirmationDialog = user.isNavigator && !user.hasManager
          const onRedirect = () => {
            if (showConfirmationDialog) {
              window.localStorage.setItem('showConfirmationDialog', 'true')
            }
          }
          const onNoRedirect = () => {
            if (showConfirmationDialog) {
              dispatch(toggleEmailConfirmationDialog('open'))
            }
          }

          dispatch(startSession({ user, environment, setViewMode }))

          if (isNavianceUser) {
            const isLinkedQuery = await refetch()

            if (isLinkedQuery.data) {
              setIsLinked(true)
              setSignInError(
                'This Headed2 account is already linked to a Naviance account. Create a new Headed2 account or log in to another Headed2 account and then try linking again'
              )
            } else {
              navigate('/naviancelinkaccount/signin')
            }
          } else {
            dispatch(
              sessionStarted({
                inDialog,
                postSigninURI,
                pathname,
                onRedirect,
                onNoRedirect
              })
            )
          }
        } else {
          /*
           * The verify membership will redirect to the accept page if not active, which will result in the
           * modal being open over it. So if we are here, we can close it
           */
          if (inDialog) {
            dispatch(closeDialog())
          }

          dispatch(verifyMembership(membership))
        }
      },
      onError: err => {
        const validation = apiErrors.getValidation(err)

        if (validation) {
          setValidation(validation)
        } else {
          setSignInError(apiErrors.getMessage(err))
        }
      }
    }
  )
  const onSubmit = useCallback(
    form => {
      setSignInError('')
      signIn(form)
    },
    [signIn]
  )
  const inRegistration = Boolean(properties?.name)

  usePageTitle(t`Signin`, inDialog)
  useEffect(() => {
    if (properties?.name) {
      setValue({ name: properties.name })
    }
  }, [properties, setValue])

  if (isUserInSessionOnMount.current && !isNavianceUser) {
    return <Redirect to="/" />
  }

  if (userProfile && !isUserInSessionOnMount.current && !isNavianceUser) {
    return (
      <Flex justifyContent="center" alignItems="center" flex="1 0 auto">
        <Text size="large" weight="bold">
          {t`Welcome ${userProfile.userDisplayName}`}
        </Text>
      </Flex>
    )
  }

  if (redirect) {
    return <Redirect to={{ ...redirect }} />
  }

  const accountTypeName = isNavianceElementary ? 'Naviance for Elementary' : 'headed2'

  return (
    <Card useDetour leftSided={!inRegistration} inDialog={inDialog}>
      <Card.Highlight color={inRegistration ? SY : isNavianceUser ? NAVIANCEBLUE : SDB}>
        <HighlightWrap isNavianceUser={isNavianceUser} isNamePanel>
          <Heading level="2" color="white">
            {inRegistration
              ? t`Your email is already registered`
              : isNavianceUser
              ? t`Link your Headed2 account to Naviance!`
              : t`Save your progress`}
          </Heading>
          <FlexWrap isNavianceUser={isNavianceUser}>
            <Text color="white" margin="0">
              {inRegistration
                ? t`There is already an account with your email assigned, please login or click "Forgot password" if you don't remember your credentials.`
                : isNavianceUser
                ? t`Link your Naviance and Headed2 accounts to access Headed2 features in Naviance.`
                : t`Login to your ${accountTypeName} account to continue your exploration where you left off.`}
            </Text>
            <Image
              isNavianceUser={isNavianceUser}
              src={
                isNavianceUser
                  ? navianceLinkedAccountSvg
                  : inRegistration
                  ? signInRegisteredSvg
                  : signInSvg
              }
              alt=""
            />
          </FlexWrap>
        </HighlightWrap>
      </Card.Highlight>
      <Card.Content fade={inRegistration}>
        {mfaPending ? (
          <>
            <Heading level="2" align="center" id="modal-title">
              {t`Verify your identity`}
            </Heading>
            <MfaChallengeForm
              name={lastUsername}
              token={mfaPending.token}
              onVerified={({ user, membership }) => {
                setMfaPending(null)

                if (user) {
                  const showConfirmationDialog = user.isNavigator && !user.hasManager
                  const onRedirect = () => {
                    if (showConfirmationDialog) {
                      window.localStorage.setItem('showConfirmationDialog', 'true')
                    }
                  }
                  const onNoRedirect = () => {
                    if (showConfirmationDialog) {
                      dispatch(toggleEmailConfirmationDialog('open'))
                    }
                  }

                  dispatch(startSession({ user, environment, setViewMode }))
                  dispatch(
                    sessionStarted({
                      inDialog,
                      postSigninURI,
                      pathname,
                      onRedirect,
                      onNoRedirect
                    })
                  )
                } else if (membership) {
                  if (inDialog) {
                    dispatch(closeDialog())
                  }

                  dispatch(verifyMembership(membership))
                }
              }}
              onCancel={() => {
                setMfaPending(null)
                setSignInError('')
              }}
            />
          </>
        ) : forgot === 'password' ? (
          <>
            <Heading level="2" align="center" id="modal-title">
              {t`Reset your Password`}
            </Heading>
            <ResetPasswordForm
              onClickLink={() => {
                dispatch(userForgotCredential('username'))
              }}
            />
          </>
        ) : forgot === 'username' ? (
          <>
            <Heading level="2" align="center" id="modal-title">
              {t`Lookup your Username`}
            </Heading>
            <LookupUsernameForm
              onClickLink={() => {
                dispatch(userForgotCredential('password'))
              }}
            />
          </>
        ) : (
          <>
            <Heading level="2" align="center" id="modal-title">
              {inDialog ? t`Sign in` : t`Welcome to ${systemName}`}
            </Heading>
            {allowInternalSignins && (
              <Form onSubmit={handleOnSubmit(onSubmit)}>
                <Alert
                  margin="0 0 15px 0"
                  type="success"
                  isVisible={Boolean(addMessage)}
                  message={addMessage}
                />
                <Alert
                  margin="0 0 15px 0"
                  type="error"
                  isVisible={Boolean(signInError)}
                  message={signInError}
                  onClose={() => setSignInError('')}
                />
                <Field
                  isRequired
                  label={t`Username`}
                  desc={error.name}
                  status={error.name ? 'error' : 'default'}>
                  <TextInput
                    size="large"
                    placeholder={t`Enter your Username`}
                    {...register('name', { required: true })}
                  />
                </Field>
                <Field
                  isRequired
                  label={t`Password`}
                  desc={error.password}
                  status={error.password ? 'error' : 'default'}>
                  <TextInput
                    size="large"
                    type="password"
                    placeholder={t`Enter your Password`}
                    {...register('password', { required: true })}
                  />
                </Field>
                <FlexForgot justifyContent="center">
                  <Link
                    href="/resetpassword"
                    weight="bold"
                    onClick={evt => {
                      evt.preventDefault()

                      if (inDialog) {
                        dispatch(userForgotCredential('password'))
                      } else {
                        navigate(evt.target.pathname)
                      }
                    }}>
                    {t`Forgot Password`}
                  </Link>
                  <Link
                    href="/lookupuser"
                    weight="bold"
                    onClick={evt => {
                      evt.preventDefault()

                      if (inDialog) {
                        dispatch(userForgotCredential('username'))
                      } else {
                        navigate(evt.target.pathname)
                      }
                    }}>
                    {t`Forgot Username`}
                  </Link>
                </FlexForgot>
                <Box alignSelf="center">
                  <Submit
                    isFullWidth
                    isFullHeight
                    type="submit"
                    size="large"
                    variant="cta"
                    align="center"
                    isLoading={isLoading}
                    isDisabled={isInvalid || isLoading}>
                    {t`Sign in`}
                  </Submit>
                </Box>
              </Form>
            )}
            {allowInternalSignins && socialLogins.length > 0 && (
              <OrBar>
                <span>Or</span>
              </OrBar>
            )}
            <Flex
              justifyContent="center"
              margin="20px 0 0 0"
              gap="15px"
              wrap="wrap"
              direction="column"
              alignItems="center">
              {socialLogins.map(item => (
                <SocialLink
                  key={item.id}
                  provider={item.id}
                  className={`btn-${item.id}`}
                  href={item.url}>
                  <span className={item.icon} />
                  <span>
                    {item.buttonText
                      ? item.buttonText
                      : t`Sign in with ${item.serviceName}`}
                  </span>
                </SocialLink>
              ))}
            </Flex>
          </>
        )}
      </Card.Content>
      {(pathname !== '/register' || !inDialog) && allowInternalRegistrations && (
        <Card.Detour>
          <Text>
            {inRegistration
              ? t`To register with a different email account,`
              : t`Don't have an account?`}{' '}
            <Link
              href="/register"
              onClick={evt => {
                evt.preventDefault()
                dispatch(userForgotCredential(''))

                if (!inRegistration && inDialog) {
                  if (isFunction(customDetourHandler)) {
                    customDetourHandler()
                  } else {
                    dispatch(openSigninDialog('createAccount'))
                  }
                }

                if (!inRegistration && !inDialog) {
                  if (isFunction(customDetourHandler)) {
                    customDetourHandler()
                  } else if (isLinked) {
                    setRedirect({ pathname: '/register', state: { panel: 'email' } })
                  } else {
                    navigate('/register')
                  }
                }

                if (inRegistration && !inDialog) {
                  setRedirect({ pathname: '/register', state: { panel: 'email' } })
                }

                if (inRegistration && inDialog) {
                  dispatch(openSigninDialog('resetRegistration'))
                }
              }}>
              {inRegistration ? t`click here` : t`Create one here`}
            </Link>
          </Text>
        </Card.Detour>
      )}
    </Card>
  )
}

SignInForm.propTypes = {
  addMessage: PropTypes.string,
  allowRedirect: PropTypes.bool,
  inDialog: PropTypes.bool,
  /** Properties from a started registration process */
  properties: PropTypes.object,
  customDetourHandler: PropTypes.func
}

export { SignInForm }
